import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { CartProvider } from "@/context/CartContext";
import { Layout } from "@/components/layout/Layout";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { SmoothScroll } from "@/components/layout/SmoothScroll";
import { api } from "@/lib/api";
import { applyImageOverrides } from "@/data/catalog";
import { applyMediaOverrides } from "@/data/pdpMedia";
import { applyTextOverrides } from "@/data/siteText";
import { captureUtm, trackPageView } from "@/lib/track";
import { MediaEditor } from "@/components/common/MediaEditor";
import { TextEditor, AutoText } from "@/components/common/TextEditor";
import { BackToTop } from "@/components/common/BackToTop";

import Home from "@/pages/Home";
import Product from "@/pages/Product";
import Shop from "@/pages/Shop";
import Candles from "@/pages/Candles";
import Science from "@/pages/Science";
import About from "@/pages/About";
import Reviews from "@/pages/Reviews";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Article from "@/pages/Article";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Policy from "@/pages/Policy";
import AdminLogin from "@/pages/AdminLogin";
import Admin from "@/pages/Admin";

function PageTracker() {
  const loc = useLocation();
  useEffect(() => {
    if (loc.pathname.startsWith("/admin")) return;
    trackPageView();
  }, [loc.pathname]);
  return null;
}

function App() {
  const [imgVersion, setImgVersion] = useState(0);

  useEffect(() => {
    captureUtm();
  }, []);

  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    Promise.all([
      api.get("/images").then(({ data }) => {
        if (data && Object.keys(data).length) applyImageOverrides(data, backend);
      }).catch(() => {}),
      api.get("/media").then(({ data }) => {
        if (data && Object.keys(data).length) applyMediaOverrides(data, backend);
      }).catch(() => {}),
      api.get("/text").then(({ data }) => {
        if (data && Object.keys(data).length) applyTextOverrides(data);
      }).catch(() => {}),
    ]).finally(() => setImgVersion((v) => v + 1));
  }, []);

  return (
    <div className="App">
      <CartProvider>
        <BrowserRouter>
          <SmoothScroll />
          <ScrollToTop />
          <PageTracker />
          <MediaEditor />
          <TextEditor />
          <AutoText />
          <BackToTop />
          <Toaster position="top-center" theme="light" />
          <Routes key={imgVersion}>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/product/:sku" element={<Product />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/candles" element={<Candles />} />
              <Route path="/science" element={<Science />} />
              <Route path="/about" element={<About />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/led-light-therapy" element={<Article />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/policies/:type" element={<Policy />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </CartProvider>
    </div>
  );
}

export default App;
